import React, { useState, useEffect } from "react";
import basiqLogo from "../../assets/basiq.png";
import basiqMark from "../../assets/basiq-mark.png";
import basiqMarkWhite from "../../assets/basiq-mark-white.png";
import Hero from "./Hero";
import TransformVisualizer from "./TransformVisualizer";
import HowItWorks from "./HowItWorks";
import SocialProof from "./SocialProof";
import Pricing from "./Pricing";
import FaqSection from "./FaqSection";
import FinalCTA from "./FinalCTA";
import { goToApp } from "../../lib/site";
import { track } from "../../lib/analytics";
import CookieConsent from "../CookieConsent";
import SiteThemeToggle from "../SiteThemeToggle";

const NAV = [
  { id: "como-funciona", label: "Como funciona" },
  { id: "prova-social", label: "Prova social" },
  { id: "planos", label: "Planos" },
  { id: "faq", label: "FAQ" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const login = (place = "header") => { track("cta_click", { cta: "login", place }); goToApp({ auth: "login" }); };
  const register = (prompt, place = "header") => { track("cta_click", { cta: "register", place, with_prompt: prompt ? 1 : 0 }); goToApp({ auth: "register", prompt }); };

  useEffect(() => {
    document.title = "Basiq — Orçamentos. Sem complicação.";
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    const planos = document.getElementById("planos");
    let seen = false;
    const io = planos && "IntersectionObserver" in window ? new IntersectionObserver((entries) => {
      if (!seen && entries.some((e) => e.isIntersecting)) { seen = true; track("view_pricing"); io.disconnect(); }
    }, { threshold: 0.3 }) : null;
    if (io) io.observe(planos);
    return () => { window.removeEventListener("scroll", onScroll); if (io) io.disconnect(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-slate-900 overflow-x-hidden" data-testid="landing-page">
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#fafafa]/85 backdrop-blur-md border-b border-slate-200/60" : "bg-transparent"}`}>
        <div className="max-w-6xl mx-auto h-16 px-5 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2" data-testid="landing-logo">
            <img src={basiqMark} alt="" className="h-[22px] w-auto object-contain" />
            <img src={basiqLogo} alt="Basiq" className="h-[19px] w-auto object-contain" />
          </button>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`navlink-${n.id}`} className="text-[13px] font-medium text-slate-500 hover:text-slate-900 px-3 py-2 rounded-full transition-colors">
                {n.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <SiteThemeToggle className="mr-1" />
            <button onClick={() => login("header")} data-testid="header-login-btn" className="text-[13px] font-medium text-slate-600 hover:text-slate-900 px-3 py-2 transition-colors">
              Entrar
            </button>
            <button onClick={() => register(undefined, "header")} data-testid="header-cta-btn" className="text-[13px] font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-full px-4 py-2 transition-all hover:scale-[1.03] active:scale-95">
              Começar grátis
            </button>
          </div>
        </div>
      </header>

      <main>
        <Hero onSubmit={(text) => register(text, "hero")} />
        <TransformVisualizer />
        <HowItWorks />
        <SocialProof onCta={() => register(undefined, "social_proof")} />
        <Pricing onCta={() => register(undefined, "pricing")} />
        <FaqSection />
        <FinalCTA onCta={() => register(undefined, "final")} />
      </main>
      <CookieConsent />

      <footer className="bg-slate-900 text-slate-400 px-5 py-14">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2">
                <img src={basiqMarkWhite} alt="" className="h-6 w-auto object-contain" />
                <img src={basiqLogo} alt="Basiq" className="h-5 w-auto object-contain brightness-0 invert" />
              </div>
              <p className="mt-3 text-[13px] text-slate-500 max-w-xs">Orçamentos. Sem complicação. Do texto ao PDF profissional em segundos — no app ou pelo WhatsApp.</p>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-[13px]">
              {NAV.map((n) => (
                <button key={n.id} onClick={() => scrollTo(n.id)} className="text-slate-400 hover:text-white transition-colors">{n.label}</button>
              ))}
              <button onClick={() => login("footer")} data-testid="footer-login-btn" className="text-slate-400 hover:text-white transition-colors">Entrar</button>
              <a href="mailto:contato@basiqorcamentos.com.br" className="text-slate-400 hover:text-white transition-colors">Suporte</a>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px]">
            <span>© {new Date().getFullYear()} Basiq. Todos os direitos reservados.</span>
            <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Sistemas operando 100%</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
